/* eslint-disable import/prefer-default-export */
import React, { useCallback, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import Link from "@mui/material/Link";
import CancelIcon from "@mui/icons-material/Cancel";
import GetAppSharpIcon from "@mui/icons-material/GetAppSharp";

interface FilePreviewProps {
  onClose: () => void;
  fileDetails: {
    name: string;
    url: string;
  };
}

const IMAGE_EXTENSIONS = ["bmp", "gif", "ico", "jpg", "jpeg", "png"];

const isPDFFile = (fileName: string): boolean => fileName.toLowerCase().endsWith(".pdf");

export const FilePreview: React.FC<FilePreviewProps> = ({ onClose, fileDetails }) => {
  const isPdf = isPDFFile(fileDetails.name);
  const extension = fileDetails.name?.slice(fileDetails.name.lastIndexOf(".") + 1);
  const isImage = IMAGE_EXTENSIONS.includes(extension.toLowerCase());

  const escFunction = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, []);

  return (
    <Box
      sx={{
        position: "absolute",
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(8, 8, 8, 0.8)",
        color: "white !important",
        padding: (theme) => theme.spacing(2, 4, 3),
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography>{fileDetails.name}</Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          {fileDetails?.url && (
            <Typography
              sx={{ cursor: "pointer", display: "flex", alignItems: "center", marginRight: "24px" }}
            >
              <GetAppSharpIcon />
              <Link
                href={fileDetails.url}
                target="_blank"
                style={{ textDecoration: "none", color: "white" }}
              >
                <p>Download</p>
              </Link>
            </Typography>
          )}
          <CancelIcon onClick={onClose} />
        </Box>
      </Box>

      {isPdf && (
        <embed
          src={fileDetails.url}
          type="application/pdf"
          title={fileDetails.name}
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      )}

      {isImage && (
        <img
          src={fileDetails.url}
          alt="File Preview"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            alignSelf: "center",
            maxHeight: "100%",
            objectFit: "contain"
          }}
        />
      )}

      {!isImage && !isPdf && (
        <Typography
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            alignSelf: "center",
            maxHeight: "100%",
            objectFit: "contain",
            height: "50%"
          }}
        >
          Preview not available
        </Typography>
      )}
    </Box>
  );
};
